/**
 * planPresentationCatalog - 套餐展示文案真相源（spec §5.5）
 *
 * 职责：
 * - tagline（每个套餐一句话定位）
 * - feature 文案（每套餐向用户展示的能力清单）
 * - 文案排序（Free -> Lite -> Pro -> Max 递进，"Everything in X" 承上启下）
 *
 * 关键约束：
 * - 动态额度（`{privateChaptersPerMonth}`）由调用方传入实际值替换，
 *   本 catalog 只定义模板字符串，不从 D1 读取额度值
 * - 服务端授权不感知本 catalog；推荐套餐展示由 commerceController 单独负责
 * - 文案只读：subscriptionPanel / paywallMarkup / authModalsMarkup 等
 *   所有展示套餐差异的地方都从这里取，禁止再硬编码第二份
 *
 * spec §5.7 删除清单（已落实）：
 * - "about N topics"（动态数字硬编码）
 * - Lite 差异化表述中的"协作"用词（capability 已下沉）
 * - "首 N 章为唯一可访问范围"（动态权益，不写死）
 * - "samples" 取代了原 "previews" 用词
 * - 未实现的离线阅读承诺
 *
 * 这 5 项违反了"权益由 D1 entitlement_grants 驱动、文案只描述真实能力"
 * 的铁律：有些把历史限制描述成特性、有些是未实现的
 * 承诺（离线阅读）、有些是会被 D1 改变的数字硬编码。
 * 本 catalog 里所有 feature 文案都只描述"该套餐确定能做什么"，
 * 不描述"该套餐不能做什么"（不能做什么由付费墙在运行时动态展示）。
 */

import type { PlanCode } from "./prototypeTypes";
import type { PlanEntitlement } from '../../domain';

/** Catalog 文案模板（spec §5.5）。 */
export const PLAN_PRESENTATION = {
	free: {
		tagline: "Start Free",
		features: [
			"Free chapters preview",
			"Article, podcast & video samples",
			"Bookmarks and learning progress",
		],
	},
	lite: {
		tagline: "Every public topic",
		features: [
			"All Free features",
			"Every public topic · read · listen · watch",
		],
	},
	pro: {
		tagline: "Private AI Create",
		features: [
			"All Lite features",
			"{privateChaptersPerMonth} private AI chapters per month",
		],
	},
	max: {
		tagline: "More Private Courses & Priority",
		features: [
			"All Pro features",
			"{privateChaptersPerMonth} private AI chapters per month",
			"Priority AI generation",
		],
	},
} as const satisfies Record<"free" | "lite" | "pro" | "max", { tagline: string; features: readonly string[] }>;

export type PlanPresentationKey = keyof typeof PLAN_PRESENTATION;

/** 可被动态替换的占位符（调用方 fillTemplate 时传入实际数值）。 */
export interface PlanTemplateValues {
	/** Pro/Max 套餐里出现的 "{privateChaptersPerMonth}"，来自 D1 entitlement_grants。 */
	privateChaptersPerMonth?: number | string;
}

/**
 * 用实际数值替换 feature 文案里的 `{xxx}` 占位符。
 *
 * 当前支持的占位符：
 * - `{privateChaptersPerMonth}` -> privateChaptersPerMonth（未传则回退为 "-"）
 *
 * 不在 catalog 上加更多占位符的设计是有意为之：每个占位符都是
 * 一个"权益数字会随套餐矩阵调整而变化"的耦合点。占位符越多，
 * catalog 与 D1 schema 的耦合面越大。保持最小集合。
 */
export function fillTemplate(template: string, values: PlanTemplateValues): string {
	return template.replace(/\{privateChaptersPerMonth\}/g, String(values.privateChaptersPerMonth ?? "-"));
}

/**
 * 获取某套餐的渲染就绪文案（占位符已替换）。
 *
 * @param plan 当前套餐（visitor 合成到 free 展示）
 * @param values 动态数值
 */
export function getPlanPresentation(
	plan: PlanCode,
	values: PlanTemplateValues = {},
	entitlement?: Pick<PlanEntitlement, 'githubDeepDive'>,
): { tagline: string; features: string[] } {
	const key: PlanPresentationKey = plan === "visitor" ? "free" : plan;
	const entry = PLAN_PRESENTATION[key];
	return {
		tagline: entry.tagline,
		features: [
			...entry.features.map((f) => fillTemplate(f, values)),
			...(entitlement?.githubDeepDive === true ? ['GitHub Trending deep dives'] : []),
		],
	};
}

/**
 * 获取某套餐的原始模板（不替换占位符）。
 *
 * 用于需要把占位符渲染成 DOM 节点（由 runtime 填值）的场景，
 * 例如 subscriptionPanel 把 `{privateChaptersPerMonth}` 渲染为
 * `<span id="${prefix}AiProFeature">AI Create: -</span>`，由
 * subscriptionChooserController 在运行时填入实际数字。
 *
 * 调用方必须自行处理 `{privateChaptersPerMonth}` 的展示。
 */
export function getPlanPresentationTemplate(plan: PlanCode): { tagline: string; features: readonly string[] } {
	const key: PlanPresentationKey = plan === "visitor" ? "free" : plan;
	return PLAN_PRESENTATION[key];
}
